<script setup lang="ts">
import type { CheckboxPassThroughOptions } from 'primevue/checkbox';
import {
  computed,
  toRef,
} from 'vue';

import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VFormInputWrapper
  from '@modules/forms/components/inputs/VFormInputWrapper/VFormInputWrapper.vue';

import type {
  VFormCheckboxEmits,
  VFormCheckboxProps,
  VFormCheckboxSlots,
} from './types';
import { useInputInnerValue } from '../composables';

const props = withDefaults(defineProps<VFormCheckboxProps>(), {});
const emit = defineEmits<VFormCheckboxEmits>();

defineSlots<VFormCheckboxSlots>();
const { confirmEdit } = useConfirmDialogs();
const { innerValue } = useInputInnerValue(toRef(props, 'value'));

const checkboxPt = computed<CheckboxPassThroughOptions>(() => ({
  box: {
    class: props.readonly ? 'transition-none checkbox-readonly' : 'transition-none',
  },
  input: {
    class: props.readonly ? 'cursor-default' : undefined,
  },
  icon: {
    class: props.readonly ? 'checkbox-readonly-icon' : undefined,
  },
}));

function onClick(): void {
  if (props.disabled || props.readonly)
    return;
  innerValue.value = !innerValue.value;
  onChange();
}

function onChange(): void {
  if (innerValue.value && props.confirm) {
    confirmEdit({
      header: props.confirm.header,
      message: props.confirm.message,
      acceptLabel: props.confirm.accept,
      rejectLabel: props.confirm.reject,
      accept: () => {
        emit('change', innerValue.value);
      },
      reject: () => {
        innerValue.value = false;
      },
      onHide: () => {
        innerValue.value = false;
      },
    });
  } else {
    emit('change', innerValue.value);
  }
}
</script>

<template>
  <VFormInputWrapper v-data-qa-auto-id="'VFormCheckbox'"
    :errors="errors"
    :hide-errors="hideErrors"
    :readonly="readonly"
    :class="isFullWidth ? 'w-full' : 'input-max-width'"
    @click.prevent.stop="onClick"
  >
    <div class="flex items-center gap-4">
      <PInputCheckbox
        v-model="innerValue"
        :input-id="name"
        :name="name"
        :readonly="readonly"
        :disabled="disabled"
        binary
        :required="required"
        :pt="checkboxPt"
        @change="onChange"
        @input="emit('input', innerValue)"
        @blur="emit('blur', innerValue)"
        @focus="emit('focus', innerValue)"
        @click.stop
      />
      <div
        v-if="label || $slots.label"
        class="flex-1"
        :class="readonly || disabled ? 'opacity-70' : 'cursor-pointer'"
      >
        <slot name="label">
          {{ label }}
        </slot>
      </div>
    </div>
  </VFormInputWrapper>
</template>
